import React, { useState, useEffect } from 'react'
import PageHeader from '../components/PageHeader'
import PageBodyContainer from '../components/PageBodyContainer'
import GroupsCreateModal from './GroupsCreateModal'
import SegmentService from 'redux/config/services/SegmentService'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import { AppState } from 'redux/config/store'
import { useSelector } from 'react-redux'
import { getAllProgramForColumn } from '../helperFunctions'
import { Segment } from 'redux/employer/employerTypes'
import DropdownV2 from 'core-system/Dropdown/DropdownV2'
import { SelectItemProps } from 'core-system/Dropdown'
import OverflowIcon from 'core-system/Icons/Misc/Overflow'
import EmployeesIcon from 'core-system/Icons/Misc/Employees'
import FlexContainer from 'core-system/FlexContainer'
import ColorPill from 'core-system/Pill/ColorPill'
import styled from 'styled-components'
import InviteUsersModal from '../components/InviteUsersModal'
import ArchiveSegmentModal from './ArchiveSegmentModal'
import SegmentEditModal from './SegmentEditModal'
import SegmentInfoDrawer from '../worksites/SegmentInfoDrawer'
import UploadDataModal from '../components/UploadDataModal'
import SegmentsDisplay from 'features/Programs/Shared/components/SegmentsDisplay'
import wordings from 'features/Programs/Shared/wordings.json'
import SegmentOperations from '../worksites/SegmentOperations'

const Alias = styled.div`
  ${(props) => props.theme.typography.action4}
  color: ${(props) => props.theme.palette.text.primary};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const TopContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1.5rem;
`

const getTableRows = (
  allSegments: Segment[],
  menuCallback: (item: SelectItemProps) => void,
  onRowClick: (segment: Segment) => void
) => {
  return allSegments.map((segment: Segment, idx: number) => {
    const selectItems = [
      { id: '0', text: 'Edit Group Info', segment: segment },
      {
        id: '1',
        text: 'Invite Commuters',
        segment: segment,
        disabled: false,
      },
      {
        id: '2',
        text: 'Archive Group',
        segment,
        disabled: false,
      },
      {
        id: '3',
        text: 'Import Commuters Data',
        segment,
        disabled: false,
      },
    ]
    return (
      <TableRow key={idx} isClickable={!segment.archived}>
        <TableRowCell onClick={() => onRowClick(segment)}>
          <Alias>{segment.name}</Alias>
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(segment)}>
          <ColorPill
            color={segment.archived ? 'gray' : 'green'}
            text={segment.archived ? 'Archived' : 'Active'}
          />
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(segment)}>
          <SegmentsDisplay
            segments={segment?.programs?.map((program) => ({
              id: program,
              text: program,
              type: program,
            }))}
            displayType='table'
          />
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(segment)}>
          <FlexContainer alignItems='center'>
            <EmployeesIcon />
            {segment.commuters}
          </FlexContainer>
        </TableRowCell>
        <TableRowCell>
          {!segment.archived && (
            <DropdownV2
              variant={'textual'}
              svgDropdown={true}
              usePortal={true}
              icon={<OverflowIcon />}
              active={null}
              items={selectItems}
              itemCallback={menuCallback}
              width={220}
              data-cy='accounts-worksite-table-dropdown'
            />
          )}
        </TableRowCell>
      </TableRow>
    )
  })
}

interface GroupsViewProps {
  activeTab: number
  setActiveTab: (tab: number) => void
  tabs: { label: string; value: number }[]
}

const GroupsView = ({ activeTab, setActiveTab, tabs }: GroupsViewProps) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const allSegments = useSelector(
    (state: AppState) => state.employer.segmentsMap
  )

  const getSegmentsWithPrograms = () => {
    return Object.values(allSegments)
      .filter((segment) => segment.type === 'CUSTOM')
      .map((segment) => {
        return {
          ...segment,
          programs: getAllProgramForColumn(
            allFlexPrograms,
            allGrhPrograms,
            allQtfPrograms,
            allMicromobilityPrograms,
            segment.id
          ),
        }
      })
  }

  const allFlexPrograms = useSelector(
    (state: AppState) => state.flex.flexPrograms
  )
  const allGrhPrograms = useSelector((state: AppState) => state.grh.grhPrograms)
  const allQtfPrograms = useSelector((state: AppState) => state.qtf.qtfPrograms)
  const allMicromobilityPrograms = useSelector(
    (state: AppState) => state.micromobility.micromobilityPrograms
  )

  const { segmentsMap } = useSelector((state: AppState) => state.employer)

  const [groupsToRender, setGroupsToRender] = useState(
    getSegmentsWithPrograms()
  )
  const [selectedGroup, setSelectedGroup] = React.useState<Segment>(null)
  const [isInviteCommutersModalOpen, setIsInviteCommutersModalOpen] =
    useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isSegmentEditModalOpen, setIsSegmentEditModalOpen] = useState(false)
  const [isGroupInfoDrawerOpen, setIsGroupInfoDrawerOpen] = useState(false)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadSegment, setUploadSegment] = useState(null)
  const [isInfoDrawerAddMode, setIsInfoDrawerAddMode] = useState(false)

  useEffect(() => {
    setGroupsToRender(getSegmentsWithPrograms())
  }, [allSegments])

  const openAddGroupModal = () => {
    setIsCreateModalOpen(true)
    SegmentService.track('addGroup-modal-click', {
      action: 'open',
    })
  }

  const closeAddGroupModal = () => {
    setIsCreateModalOpen(false)
    SegmentService.track('addGroup-modal-action', {
      action: 'close',
    })
  }

  const HeaderCell = (text: string) => (
    <TableRowCell>
      <Text fontWeight={600} variant={'action3'}>
        {text}
      </Text>
    </TableRowCell>
  )

  const headerCells = [
    HeaderCell('Group'),
    HeaderCell('Status'),
    HeaderCell('Programs'),
    HeaderCell('Commuters'),
    HeaderCell('Edit'),
  ]

  const menuCallback = (item: SelectItemProps) => {
    setSelectedGroup(item.segment)
    if (item.id === '0' && !item.disabled) {
      setIsSegmentEditModalOpen(true)
    }
    if (item.id === '1' && !item.disabled) {
      setIsInviteCommutersModalOpen(true)
    }
    if (item.id === '2' && !item.disabled) {
      setIsDeleteModalOpen(true)
    }
    if (item.id === '3' && !item.disabled) {
      handleUploadModalOpen(item.segment.id)
    }
  }

  const filters = [
    {
      header: 'Status',
      checkboxes: [
        { label: 'Active', checked: false, key: 'archived', value: false },
        { label: 'Archived', checked: false, key: 'archived', value: true },
      ],
    },
    {
      header: 'Programs',
      checkboxes: [
        {
          label: wordings.flex.title,
          checked: false,
          key: 'programs',
          value: wordings.flex.title,
        },
        {
          label: wordings.grh.title,
          checked: false,
          key: 'programs',
          value: wordings.grh.title,
        },
        {
          label: wordings.qtf.title,
          checked: false,
          key: 'programs',
          value: wordings.qtf.title,
        },
        {
          label: wordings.micromobility.title,
          checked: false,
          key: 'programs',
          value: wordings.micromobility.title,
        },
      ],
    },
  ]

  const onRowClick = (segment: Segment) => {
    if (segment.archived) {
      return
    }
    setSelectedGroup(segment)
    setIsGroupInfoDrawerOpen(true)
  }

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false)
  }

  const handleUploadModalOpen = (segment?: string) => {
    if (segment) {
      setUploadSegment(segmentsMap[segment])
    }
    setIsUploadModalOpen(true)
  }

  const openCommutersAddModal = (segment?: string) => {
    if (segment) {
      setSelectedGroup(segmentsMap[segment])
    }
    setIsInfoDrawerAddMode(true)
    setIsGroupInfoDrawerOpen(true)
  }

  return (
    <>
      <PageHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
        newGroupWorksiteButtonText='Create New Group'
        showCreateNewGroupButton
        createNewGroupWorksite={openAddGroupModal}
      />
      <PageBodyContainer>
        <TopContainer>
          <SegmentOperations
            filters={filters}
            searchArray={['name']}
            segments={getSegmentsWithPrograms()}
            setFilteredSegments={setGroupsToRender}
            sortKey='name'
          />
        </TopContainer>
        <Table
          headerCells={headerCells}
          rows={getTableRows(groupsToRender, menuCallback, onRowClick)}
          stickyColumns={1}
        />
      </PageBodyContainer>
      <GroupsCreateModal
        open={isCreateModalOpen}
        closeModal={closeAddGroupModal}
        handleUploadModalOpen={handleUploadModalOpen}
        openCommutersAddModal={openCommutersAddModal}
      />
      {selectedGroup && (
        <>
          <InviteUsersModal
            segmentId={selectedGroup.id}
            closeModal={() => setIsInviteCommutersModalOpen(false)}
            open={isInviteCommutersModalOpen}
          />
          <ArchiveSegmentModal
            open={isDeleteModalOpen}
            closeModal={() => setIsDeleteModalOpen(false)}
            segment={selectedGroup}
          />
          <SegmentEditModal
            open={isSegmentEditModalOpen}
            closeModal={() => {
              setIsSegmentEditModalOpen(false)
            }}
            segment={selectedGroup}
          />
          <SegmentInfoDrawer
            type='group'
            open={isGroupInfoDrawerOpen}
            closeModal={() => setIsGroupInfoDrawerOpen(false)}
            segment={selectedGroup}
            commutersAddMode={isInfoDrawerAddMode}
          />
        </>
      )}
      <UploadDataModal
        open={isUploadModalOpen}
        closeModal={handleUploadModalClose}
        segment={uploadSegment}
      />
    </>
  )
}

export default React.memo(GroupsView)
